import {action, makeObservable, observable, override, runInAction} from "mobx";
import {createTheme} from "@mui/material/styles";
import type {
    GridApi,
    GridColDef,
    GridColumnVisibilityModel, GridFilterOperator,
    GridRowGroupingModel,
    GridSortModel, GridValidRowModel
} from "@mui/x-data-grid-premium";
import {GRID_TREE_DATA_GROUPING_FIELD} from "@mui/x-data-grid-premium";
import type ProfileStore from "../../../stores/ProfileStore.tsx";
import type AuthStore from "../../../authentication/AuthStore.tsx";
import {
    BaseMuixTableController,
    type MuixColumnSettings
} from "../../../components/muix-data-grid/controllers/BaseMuixTableController.tsx";
import type {JSONSerializableGridColDef} from "../../../components/muix-data-grid/MuiXColumnDefinitions.ts";
import type {Optional} from "../../../utils/ts-utils.ts";
import type {ApiCategory} from "../../../services/classes/Category.ts";
import {categoryExportString} from "../../../services/classes/Category.ts";
import {CategoryReadonlyCell} from "../../../components/muix-data-grid/cells/CategoryReadonlyCell.tsx";
import {CurrencyCell} from "../../../components/muix-data-grid/cells/CurrencyCell.tsx";
import {CategoryL1Cell} from "../../../components/muix-data-grid/cells/CategoryL1Cell.tsx";
import type {BagStore} from "../../../stores/BagStore.tsx";
import type {GRPageController} from "./GRPageController.ts";
import {JsonPropertiesCell} from '../components/JsonPropertiesCell/JsonPropertiesCell.tsx';
import {JsonPropertiesTagLayout} from '../components/JsonPropertiesCell/JsonPropertiesTagLayout.tsx';
import {GoldenRecordClusterType} from "../../../services/ApiTypes.ts";

export const PROPERTY_TYPE_FILTER_OPERATORS: GridFilterOperator<GridValidRowModel, number>[] = [
    {
        label: 'Filtered by grouping settings panel',
        value: 'isAnyOf',
        getApplyFilterFn: () => null, // Backend only
        requiresFilterValue: false,
    },
];

export class GRTableController extends BaseMuixTableController {
    /**
     * Max measured width (px) needed for the Properties column so every loaded row can show at least
     * the first tag plus a "+N" chip (Material harmonization only). Drives `minWidth` on that column.
     */
    materialPropertiesContentMinWidth = 0;

    private propertiesTagMeasureLayout: JsonPropertiesTagLayout | null = null;

    constructor(
        private controller: GRPageController,
        private auth: AuthStore,
        private profile: ProfileStore,
        protected bagStore: BagStore,
    ) {
        super(bagStore, 'MHTC');
        makeObservable(this, {
            tableColumns: override,
            tableAggregationOnlyColumns: override,
            defaultSortModel: override,
            rowGroupingModel: override,
            visibilityModel: override,
            setAutoResize: override,
            materialPropertiesContentMinWidth: observable,
            refreshMaterialPropertiesMinWidthFromGridApi: action,
            // groupingColDef: override,
        });
        // Override defaults
        runInAction(() => {
            this.muixGridGroupModel = [];
            this.autoResize = true;
        });
        const vm = this.muiXGridColumnVisibilityModel ?? this.getColumnVisibilityModel();
        if (Object.keys(vm).length === 0) {
            runInAction(() => {
                this.muiXGridColumnVisibilityModel = {
                    'cat': false,
                };
                for (const col of this.tableAggregationOnlyColumns) {
                    this.muiXGridColumnVisibilityModel[col.field] = false;
                }
                this.storeColumnVisibilityModelChange(this.muiXGridColumnVisibilityModel);
            });
        } else {
            runInAction(() => this.muiXGridColumnVisibilityModel = this.getColumnVisibilityModel());
        }
        // Aggregation model is forced, user cannot change it
        runInAction(() => this.muixGridAggregationModel = {
            p_spend: 'sum',
            p_local_spend: 'sum',
            p_unit_spend: 'sum',
            p_quantity: 'sum',
        });
    }

    private get isMaterialHarmonizationCluster(): boolean {
        return (
            this.controller.filterController.cluster_type ===
            GoldenRecordClusterType.Material
        );
    }

    private getPropertiesTagMeasureLayout(): JsonPropertiesTagLayout {
        if (!this.propertiesTagMeasureLayout) {
            this.propertiesTagMeasureLayout = new JsonPropertiesTagLayout(
                createTheme(),
            );
        }
        return this.propertiesTagMeasureLayout;
    }

    /**
     * Scans rows currently in the grid and sets {@link materialPropertiesContentMinWidth} so the
     * Properties column can fit the worst-case row (first tag + "+N") — Material cluster only.
     */
    refreshMaterialPropertiesMinWidthFromGridApi() {
        if (!this.isMaterialHarmonizationCluster || !this.gridApi) {
            this.materialPropertiesContentMinWidth = 0;
            return;
        }
        const layout = this.getPropertiesTagMeasureLayout();
        let maxW = 0;
        for (const id of this.gridApi.getAllRowIds()) {
            const row = this.gridApi.getRow(id);
            if (!row || typeof row !== "object") continue;
            const props = (row as GridValidRowModel)["properties"];
            const w = layout.minColumnWidthForFirstTagAndOverflow(props);
            if (w > maxW) maxW = w;
        }
        this.materialPropertiesContentMinWidth = maxW;
    }

    /** Fields for `autosizeColumns({ columns })` — every column except those with a saved width in localStorage. */
    private getColumnFieldsForAutosizeOnly(settings: MuixColumnSettings): string[] {
        if (!this.gridApi) return [];
        return this.gridApi
            .getAllColumns()
            .map((c) => c.field)
            .filter((field) => {
                if (field === GRID_TREE_DATA_GROUPING_FIELD) {
                    const w = settings.columns[GRID_TREE_DATA_GROUPING_FIELD]?.width;
                    return !(w != null && w > 0);
                }
                const stored = this.rowGroupingModel.includes(field)
                    ? settings.columns[GRID_TREE_DATA_GROUPING_FIELD]
                    : settings.columns[field];
                return !(stored?.width != null && stored.width > 0);
            });
    }

    override scheduleAutosizeColumnsAfterPaint() {
        requestAnimationFrame(() =>
            requestAnimationFrame(() => {
                if (!this.gridApi) return;
                this.refreshMaterialPropertiesMinWidthFromGridApi();
                const runAutosize = () => {
                    if (!this.gridApi) return;
                    if (this.autoResize) {
                        this.runAutosizeColumns();
                    } else {
                        const columns = this.getColumnFieldsForAutosizeOnly(
                            this.getColumnSettings(),
                        );
                        if (columns.length === 0) return;
                        this.runAutosizeColumns(columns);
                    }
                };
                // Let React apply updated Properties `minWidth` before autosize measures columns.
                if (this.isMaterialHarmonizationCluster) {
                    requestAnimationFrame(() =>
                        requestAnimationFrame(runAutosize),
                    );
                } else {
                    runAutosize();
                }
            }),
        );
    }

    override setAutoResize(value: boolean) {
        this.autoResize = value;
        if (this.autoResize && this.gridApi) {
            this.scheduleAutosizeColumnsAfterPaint();
        } else if (!this.autoResize && this.gridApi) {
            requestAnimationFrame(() =>
                requestAnimationFrame(() => {
                    this.restoreColumnWidthsFromLocalStorage();
                    this.scheduleAutosizeColumnsAfterPaint();
                }),
            );
        }
    }

    /**
     * After each data batch, run autosize on the next `rowsSet` — full width when {@link autoResize}
     * is on, otherwise only columns without a user-saved width.
     */
    protected override subscribeAutosizeAfterRowsSet(gridApi: GridApi) {
        const unsubscribe = gridApi.subscribeEvent("rowsSet", () => {
            unsubscribe();
            this.scheduleAutosizeColumnsAfterPaint();
        });
    }

    get tableColumns(): GridColDef[] {
        const columns: JSONSerializableGridColDef[] = [{
            field: 's_name',
            serializableType: 'string',
            headerName: this.profile.supplierDataName,
            groupable: true,
            sortable: !this.isGroupBy,
            minWidth: 150,
            headerAlign: 'left',
            align: 'left',
        }, {
            field: 'harmonized_name',
            serializableType: 'string',
            headerName: GoldenRecordClusterType[this.controller.filterController.cluster_type] + ' Group',
            groupable: true,
            sortable: !this.isGroupBy,
            minWidth: 200,
            headerAlign: 'left',
            align: 'left',
        }, {
            field: 'p_unit_spend',
            serializableType: 'number-currency',
            type: 'number',
            headerName: 'Unit spend',
            groupable: false,
            renderCell: CurrencyCell,
            minWidth: 120,
            headerAlign: 'left',
            align: 'left',
        }, {
            field: 'p_quantity',
            serializableType: 'number',
            type: 'number',
            headerName: 'Quantity',
            groupable: false,
            minWidth: 120,
            headerAlign: 'left',
            align: 'left',
        }, {
            field: 'p_spend',
            serializableType: 'number-currency',
            type: 'number',
            headerName: 'Spend',
            groupable: false,
            renderCell: CurrencyCell,
            minWidth: 120,
            headerAlign: 'left',
            align: 'left',
        }, {
            field: 'properties',
            serializableType: 'string',
            headerName: 'Properties',
            groupable: true,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: JsonPropertiesCell,
            minWidth: this.isMaterialHarmonizationCluster
                ? Math.max(120, this.materialPropertiesContentMinWidth)
                : 120,
            headerAlign: 'left',
            align: 'left',
        }, {
            field: 'bu_name',
            serializableType: 'string',
            headerName: this.profile.buDataName,
            groupable: false,
            sortable: !this.isGroupBy,
            minWidth: 130,
            headerAlign: 'left',
            align: 'left',
        }, {
            field: 'cat',
            serializableType: 'custom-cat',
            headerName: 'Category',
            groupable: false,
            minWidth: 200,
            renderCell: CategoryReadonlyCell,
            renderHeaderFilter: () => null,
            sortable: false,
            valueFormatter: (value: Optional<ApiCategory, 'id'>) => categoryExportString(value),
            headerAlign: 'left',
            align: 'left',
        }, {
            // Include this field to enable filtering. If is not included, filter button in toolbar would crash
            field: 'type_id',
            serializableType: 'number',
            headerName: 'Property type',
            filterOperators: PROPERTY_TYPE_FILTER_OPERATORS,
            filterable: false,
        }] as JSONSerializableGridColDef[];
        // Add columns from col config API
        const columnFileds = columns.map(c => c.field);
        columns.push(...(this.bagStore.configStore.databagConfig?.columns ?? []).filter(c =>
            c.mh_in_use &&
            ['data_source_part', 'data_source_supplier', 'data_source_business_unit'].includes(c.table_name) &&
            !columnFileds.includes(c.column_name)
        ).map(c => ({
            field: c.column_name,
            serializableType: 'string',
            headerName: c.display_name || c.column_name,
        } as JSONSerializableGridColDef)));
        return this.applyColumnOrdering(
            this.applyColumnSettings(
                columns.concat(this.tableAggregationOnlyColumns).map(
                    c => ({...c, aggregable: !!c['aggregable']})
                )
            )
        );
    }

    get tableAggregationOnlyColumns(): JSONSerializableGridColDef[] {
        const columns = [{
            field: 'cat_l1_lst',
            serializableType: 'string',
            headerName: 'Unique L1',
            minWidth: 200,
            renderCell: CategoryL1Cell,
            sortable: false,
            disableColumnMenu: true,
        }, {
            field: 'supplier_id_cnt',
            serializableType: 'number',
            headerName: 'Unique Suppliers',
        }, {
            field: 'bu_id_cnt',
            serializableType: 'number',
            headerName: 'Unique BUs',
        }, {
            field: 'part_id_cnt',
            serializableType: 'number',
            headerName: 'Unique transactions',
        }, {
            field: 'part_p_name_cnt',
            serializableType: 'number',
            headerName: 'Unique SKU',
        }] as JSONSerializableGridColDef[];
        return columns.map(c => ({
            ...c,
            pinnable: false,
            filterable: false,
            groupable: false,
            aggregable: false,
        }));
    }

    get defaultSortModel(): GridSortModel | undefined {
        return [{field: 'p_spend', sort: 'desc'}];
    }

    get rowGroupingModel(): GridRowGroupingModel {
        return (this.muixGridGroupModel ?? this.getGroupingModel()).concat(
            (this.controller.filterController.selected_cluster_id &&
                this.controller.filterController.cluster_type == GoldenRecordClusterType.Material) ?
                ['selected_properties'] : []
        );
    }

    get visibilityModel(): GridColumnVisibilityModel {
        const model = super.visibilityModel;
        model['type_id'] = false;
        (this.bagStore.configStore.databagConfig?.columns ?? []).filter(c =>
            c.mh_in_use &&
            !c.mh_default_visibility &&
            !(c.column_name in model) &&
            ['data_source_part', 'data_source_supplier', 'data_source_business_unit'].includes(c.table_name)
        ).forEach(c => model[c.column_name] = false);
        return model;
    }
}